/**
 * The device panel's **Modules** tab (MODULES §8.7).
 *
 * Two halves, and both come from the same manifest: what the phone last said
 * about each module it carries (the heartbeat's `modules`, §4.4), and one
 * button per command it declares, with a form generated from that command's
 * `args` schema (§2.8). Nothing here knows what a module *is* — a release with
 * a new one grows a card and a form without a line changing.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, api } from "../api/client";
import type { ModuleArg, ModuleBeat } from "../api/types";
import { ErrorBox } from "../components/common";
import { useI18n, type Translate } from "../i18n";
import { localized } from "../i18n/localized";

/** What each `kind` types into: a link is a link on a phone too (§2.8). */
const ARG_INPUTS: Partial<Record<ModuleArg["kind"], string>> = {
  int: "number",
  float: "number",
  url: "url",
};

/** The value a form starts at: the manifest's default, or an empty field. */
function initialArgs(args: Record<string, ModuleArg>): Record<string, string | boolean> {
  return Object.fromEntries(
    Object.entries(args).map(([name, spec]) => [
      name,
      spec.kind === "bool" ? spec.default === true : (spec.default?.toString() ?? ""),
    ]),
  );
}

/**
 * A form's fields back to the JSON the endpoint takes. An empty optional
 * field is *left out* rather than sent empty: the server fills the default,
 * and "" is not a number.
 */
function toBody(
  args: Record<string, ModuleArg>,
  values: Record<string, string | boolean>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [name, spec] of Object.entries(args)) {
    const value = values[name];
    if (spec.kind === "bool") {
      body[name] = value === true;
      continue;
    }
    if (typeof value !== "string" || value === "") continue;
    body[name] = spec.kind === "int" || spec.kind === "float" ? Number(value) : value;
  }
  return body;
}

/** `module_bad_arg` issues carry `args.<name>`; the field says its own. */
function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !error.issues) return {};
  const byField: Record<string, string> = {};
  for (const issue of error.issues) {
    const name = issue.path.startsWith("args.") ? issue.path.slice("args.".length) : "";
    if (name) byField[name] = issue.message;
  }
  return byField;
}

/** Every refusal a customer can act on, worded (MODULES §8.3). */
function refusal(error: unknown, module: string, t: Translate): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === "module_not_on_device") return t("panel.moduleNotOnDevice", { module });
  if (error.code === "device_offline") return t("panel.moduleOffline");
  if (error.code === "module_command_failed") {
    return t("panel.moduleFailed", { message: String(error.detail?.message ?? error.message) });
  }
  return null;
}

function CommandForm({
  deviceId,
  moduleId,
  command,
  args,
  onClose,
}: {
  deviceId: string;
  moduleId: string;
  command: string;
  args: Record<string, ModuleArg>;
  onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const [values, setValues] = useState(() => initialArgs(args));

  const run = useMutation({
    mutationFn: () =>
      api.deviceModuleCommand(deviceId, moduleId, command, toBody(args, values)),
  });
  const errors = fieldErrors(run.error);
  const said = refusal(run.error, moduleId, t);

  return (
    <form
      className="module-command-form"
      onSubmit={(event) => {
        event.preventDefault();
        run.mutate();
      }}
    >
      {Object.entries(args).map(([name, spec]) => {
        // The manifest's own label, and the bare id only when it has none to
        // give (MODULES §2.8) — the id stays beside it either way, because it
        // is what an ack and an issue path call the field.
        const label = localized(spec.label, lang, name);
        const description = localized(spec.description, lang);
        return (
          <label key={name} className="field">
            <span className="field-label">
              <span className={label === name ? "mono" : undefined}>{label}</span>
              {label !== name && <span className="muted small mono"> {name}</span>}
              {spec.required && <span className="error-text"> *</span>}
            </span>
            {spec.kind === "bool" ? (
              <input
                type="checkbox"
                aria-label={label}
                checked={values[name] === true}
                onChange={(event) =>
                  setValues((last) => ({ ...last, [name]: event.target.checked }))
                }
              />
            ) : spec.kind === "enum" ? (
              <select
                aria-label={label}
                value={String(values[name] ?? "")}
                onChange={(event) => setValues((last) => ({ ...last, [name]: event.target.value }))}
              >
                {!spec.required && <option value="" />}
                {(spec.choices ?? []).map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
            ) : (
              <input
                aria-label={label}
                type={ARG_INPUTS[spec.kind] ?? "text"}
                inputMode={spec.kind === "url" ? "url" : undefined}
                placeholder={spec.kind === "url" ? "https://" : undefined}
                step={spec.kind === "float" ? "any" : undefined}
                min={spec.min}
                max={spec.max}
                required={spec.required}
                value={String(values[name] ?? "")}
                onChange={(event) => setValues((last) => ({ ...last, [name]: event.target.value }))}
              />
            )}
            {errors[name] && <span className="error-text small">{errors[name]}</span>}
            {description && <span className="muted small">{description}</span>}
          </label>
        );
      })}

      <div className="row">
        <button className="primary" type="submit" disabled={run.isPending}>
          {run.isPending ? t("panel.moduleRunning") : t("panel.moduleRun")}
        </button>
        <button type="button" onClick={onClose}>
          {t("panel.moduleClose")}
        </button>
      </div>

      {said ? (
        <p className="error-text small">{said}</p>
      ) : (
        run.error != null && Object.keys(errors).length === 0 && <ErrorBox error={run.error} />
      )}
      {run.data !== undefined && (
        <pre className="module-ack mono small">{JSON.stringify(run.data, null, 2)}</pre>
      )}
    </form>
  );
}

export default function DeviceModules({
  deviceId,
  beats,
}: {
  deviceId: string;
  /** The phone's own account of the modules its APK carries (§4.4). */
  beats: Record<string, ModuleBeat>;
}) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState<string | null>(null);
  // The catalogue supplies the labels; a module the current release no longer
  // carries still shows, by id, because the phone still has it.
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });
  const catalogue = modules.data ?? [];

  const entries = Object.entries(beats).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return (
      <div className="side-body">
        <p className="muted" style={{ margin: 0 }}>
          {t("panel.noModules")}
        </p>
      </div>
    );
  }

  return (
    <div className="side-body">
      {entries.map(([moduleId, beat]) => {
        const module = catalogue.find((entry) => entry.id === moduleId);
        const commands = Object.entries(module?.commands ?? {});
        return (
          <section key={moduleId} className="module-beat">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h3 style={{ margin: 0 }}>{localized(module?.name, lang, moduleId)}</h3>
              <span className={`module-state ${beat.state}`}>
                {t(`panel.moduleState.${beat.state}`)}
              </span>
            </div>
            <p className="muted small mono" style={{ margin: "2px 0 8px" }}>
              {moduleId} {beat.version}
            </p>
            {beat.message && <p className="error-text small">{beat.message}</p>}

            <ul className="module-values">
              {Object.entries(beat.values ?? {}).map(([valueId, text]) => (
                <li key={valueId}>
                  <span className="muted">
                    {localized(module?.values?.[valueId]?.label, lang, valueId)}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>

            {commands.length > 0 && (
              <div className="row module-commands">
                {commands.map(([command]) => (
                  <button
                    key={command}
                    type="button"
                    className={open === `${moduleId}/${command}` ? "on" : ""}
                    onClick={() =>
                      setOpen((last) =>
                        last === `${moduleId}/${command}` ? null : `${moduleId}/${command}`,
                      )
                    }
                  >
                    {command}
                  </button>
                ))}
              </div>
            )}
            {commands.map(([command, spec]) =>
              open === `${moduleId}/${command}` ? (
                <CommandForm
                  key={command}
                  deviceId={deviceId}
                  moduleId={moduleId}
                  command={command}
                  args={spec.args ?? {}}
                  onClose={() => setOpen(null)}
                />
              ) : null,
            )}
          </section>
        );
      })}
    </div>
  );
}
